// «Мои сделки», «Мои задачи» (R-U30, decision 040 п.5, as in amoCRM): the person's records still in work, by the date of the next
// action — a board whose drop sets the date, or a list grouped the same way with bulk actions. A page of its own, not a filter of a view.

import { useMemo, useState } from 'react';
import { canRead, canWrite, recordTitle, textKey, type Id, type SortKey, type ViewColumn, type ViewDef, accountToday } from '@confdeck/core-shared';
import { Board } from '../board/Board.tsx';
import { newRecordData } from '../record/new-record.ts';
import { createRecord, freshRecords } from '../sync/mutations.ts';
import { useAccess, useDebounced, useMeta, useQuery, useSync } from '../sync/react.tsx';
import { BulkEdit } from '../table/BulkEdit.tsx';
import { DataTable, plural, type TableGroup } from '../table/DataTable.tsx';
import { navigate, recordHref, wantsNewTab } from '../shell/router.ts';
import { HERE_LIMIT, useScreenSearch, type ScreenHit } from '../shell/screen-search.ts';
import { Segmented } from '../ui/controls.tsx';
import { Button, Count } from '../ui/primitives.tsx';
import { TopBar, TopTitle } from '../ui/TopBar.tsx';
import { dueBucket, DUE_BUCKETS } from './due-buckets.ts';
import { mineFilter, mineHref, pipelineField, pipelineSectionOf, stagesBy, type BulkKind } from './pipeline-section.ts';
import shell from '../shell/shell.module.css';

/** The columns of a board or a list made for a page, not saved: a view in memory with the section's fields. */
export function pageView(typeId: Id, id: string, name: string, layout: ViewDef['layout'], fields: string[], groupBy: ViewDef['groupBy'], sort: ViewDef['sort'] = []): ViewDef {
  return { id, kind: 'view', rev: 0, typeId, name, layout, filter: null, sort, groupBy, columns: fields.map((f, i) => ({ field: f, width: i === 0 ? 260 : 160 })), shared: false, ownerId: null, order: 0, archivedAt: null } as ViewDef;
}

export function MineScreen({ typeKey, layout }: { typeKey: string; layout: 'board' | 'list' }) {
  const meta = useMeta();
  const client = useSync();
  const access = useAccess();
  const type = meta.typeByKey(typeKey);
  const def = pipelineSectionOf(typeKey);
  const field = type ? pipelineField(meta, type.id) : undefined;
  const me = client.header?.userId;
  const [searchText, setSearch] = useState('');
  const search = useDebounced(searchText, 80);
  const [selected, setSelected] = useState<Set<Id>>(new Set());
  const [bulk, setBulk] = useState<BulkKind | null>(null);
  const filter = useMemo(() => (def && field ? mineFilter(meta, def, field, me) : null), [def, field, me, meta, meta.all.size]);
  // The list's own columns and sort, for this visit (MiniMax review wave A, P3-1): the groups stay the buckets, the sort orders inside.
  const [own, setColumns] = useState<ViewColumn[] | null>(null);
  const columns = own ?? (type && def ? pageView(type.id, '', '', 'table', [type.titleField, ...def.list], null).columns : []);
  const [sort, setSort] = useState<SortKey[]>([]);
  const q = useQuery(type?.id ?? '', filter, sort.length ? sort : def ? [{ field: def.due, dir: 'asc' }] : [], search);
  const today = accountToday(meta);
  const groups: TableGroup[] = useMemo(() => {
    if (!def) return [];
    const by = new Map<string, Id[]>(DUE_BUCKETS.map((b) => [b.key, []]));
    for (const id of q.ids) by.get(dueBucket(client.store.record(id)?.data[def.due], today))!.push(id);
    return DUE_BUCKETS.map((b) => ({ key: b.key, label: b.label, color: b.color, ids: by.get(b.key)! }));
  }, [q.ids, q.version, def, today, client.store]);

  useScreenSearch(type ? (query) => {
    const k = textKey(query);
    const hits: ScreenHit[] = [];
    for (const id of q.ids) {
      const title = recordTitle(client.store.record(id), meta);
      if (textKey(title).includes(k)) hits.push({ id, recordId: id, label: title, hint: def?.words.mine, icon: 'mine', run: () => navigate(recordHref(id)) });
      if (hits.length >= HERE_LIMIT) break;
    }
    return hits;
  } : null);

  if (!type || !def || !field) return <div className={shell.empty}>Раздел не найден</div>;
  if (!canRead(access, type.id)) return <div className={shell.empty}>Нет доступа к разделу «{type.nameMany}».</div>;
  const open = (id: Id, e?: React.MouseEvent | React.KeyboardEvent) => (wantsNewTab(e as React.MouseEvent) ? window.open(recordHref(id), '_blank') : navigate(recordHref(id)));
  const writable = canWrite(access, type.id);
  const create = () => {
    // A new record of «Мои» is the person's own: their name in the field of «мои», the first stage of the first pipeline.
    const data = { ...newRecordData(meta, type.id, 'Без названия'), ...(def.mine !== 'ownerId' && me ? { [def.mine]: me } : {}) };
    const id = createRecord(client, type.id, data);
    freshRecords.add(id);
    open(id);
  };
  const won = (id: Id) => {
    // «Выполнить»: the record goes to the «won» final of its own pipeline.
    const stage = meta.term(String(client.store.record(id)?.data[field.key] ?? ''));
    const target = stage?.parentId ? stagesBy(meta, field, 'won', stage.parentId)[0] : undefined;
    // A category without a «won» stage has nowhere to put it: said in the outcome, not counted as unchanged (audit wave A, P3-2).
    if (!target) return { refuse: `В «${meta.term(stage?.parentId ?? '')?.name ?? 'категории'}» нет итогового этапа «успех»` };
    return target !== stage?.id ? { [field.key]: target } : null;
  };
  const action = def.bulk.find((b) => b.kind === bulk);
  const view = pageView(type.id, `mine:${type.id}`, def.words.mine, 'board', [type.titleField, ...def.card], { field: def.due, dates: 'due' });

  return (
    <div className={shell.typeScreen}>
      <TopBar heading={def.words.mine} start={<TopTitle icon="mine">{def.words.mine}</TopTitle>}
        middle={<Segmented size="sm" label="Вид" value={layout} onChange={(v) => navigate(mineHref(typeKey, v === 'list' ? 'list' : undefined), { replace: true })}
          items={[{ value: 'board', label: 'Доска' }, { value: 'list', label: 'Список' }]} />}
        search={{ value: searchText, onChange: setSearch, placeholder: `Найти в «${def.words.mine}»`, slash: true }}
        count={<Count value={q.ids.length.toLocaleString('ru-RU')} />}
        actions={writable ? [{ key: 'create', label: type.name, icon: 'plus', primary: true, onClick: create }] : []} />
      <div className={shell.typeBody}>
        <div className={shell.listArea}>
          {/* The board stays with its empty columns, as in amoCRM: a new record is dragged there; the list says it is empty. */}
          {q.ids.length === 0 && layout === 'list' ? (
            <div className={shell.empty}><div>{search ? 'Ничего не нашлось.' : `В «${def.words.mine}» пусто: здесь — открытые записи, где вы ${def.mine === 'ownerId' ? 'ответственный' : 'исполнитель'}.`}</div></div>
          ) : layout === 'board' ? (
            <Board typeId={type.id} view={view} ids={q.ids} onOpen={open} openId={null} />
          ) : (
            <DataTable typeId={type.id} ids={q.ids} groups={groups} columns={columns} onColumns={setColumns}
              sortState={sort} onSort={(f, dir) => setSort(dir ? [{ field: f, dir }] : [])} onOpen={open} selected={selected} onSelect={setSelected} />
          )}
          {layout === 'list' && selected.size > 0 && (
            <div className={shell.bulk} role="toolbar" aria-label="Действия с выбранными">
              <span>Выбрано {selected.size} {plural(selected.size, 'запись', 'записи', 'записей')}</span>
              {writable && def.bulk.map((b) => <Button key={b.kind} size="sm" onClick={() => setBulk(b.kind)}>{b.label}</Button>)}
              <Button size="sm" icon="close" onClick={() => setSelected(new Set())}>Снять выбор</Button>
            </div>
          )}
          {action && <BulkEdit typeId={type.id} ids={[...selected]} title={action.label} onClose={() => { setBulk(null); setSelected(new Set()); }}
            only={action.kind === 'stage' ? [field.key] : action.kind === 'owner' ? [def.mine] : action.kind === 'due' ? [def.due] : undefined}
            preset={action.kind === 'won' ? { label: action.label, data: won } : undefined} />}
        </div>
      </div>
    </div>
  );
}
